import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
  CardMedia,
  Menu,
} from "@mui/material";
import CommonModal from "../../../components/common/CommonModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PublicIcon from "@mui/icons-material/Public";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import TitleIcon from "@mui/icons-material/Title";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

import EnhancedTable from "../../../components/common/EnhancedTable";
import { useBlogs } from "../../../hooks/useBlogs";
import { useError } from "../../../contexts/ErrorContext";
import { Blog, BlogRequest } from "../../../types/blogs";

const formatDateDdMmYyyy = (value: any): string => {
  if (!value) return "—";

  if (Array.isArray(value)) {
    const [y, m, d] = value;
    return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
  }

  if (typeof value === "string") {
    if (value.includes(",")) {
      const parts = value.split(",").map(Number);
      if (parts.length >= 3) {
        const [y, m, d] = parts;
        return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
      }
    }
    const dt = new Date(value);
    if (!Number.isNaN(dt.getTime())) {
      const d = dt.getDate();
      const m = dt.getMonth() + 1;
      const y = dt.getFullYear();
      return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
    }
  }

  return "—";
};

const truncateDescription = (value: any): string => {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  if (!text) return "...";
  return `${text.slice(0, 50)}...`;
};

const normalizeUploadedImageUrl = (url: string): string => {
  if (!url || /^https?:\/\//i.test(url)) return url;
  const baseUrl = process.env.REACT_APP_API_URL || "";
  return `${baseUrl}${url.startsWith("/") ? url : `/${url}`}`;
};

/** Maps backend status enum values to user-facing display labels. */
const statusLabelMap: Record<string, string> = {
  DRAFT: 'UNPUBLISHED',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED',
};

const getStatusLabel = (status: string | undefined | null): string => {
  const key = String(status ?? 'DRAFT');
  return statusLabelMap[key] ?? key;
};

interface BlogActionMenuProps {
  row: Blog;
  onView: (blog: Blog) => void;
  onEdit: (blog: Blog) => void;
  onDelete: (id: number) => void;
  onTogglePublish: (blog: Blog) => void;
}

const BlogActionMenu: React.FC<BlogActionMenuProps> = ({
  row,
  onView,
  onEdit,
  onDelete,
  onTogglePublish,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        color="default"
        title={t("actions", "Actions") as string}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          elevation: 1,
          sx: {
            minWidth: 150,
            boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
            border: "1px solid #e0e0e0",
          },
        }}
      >
        <MenuItem
          onClick={(e) => {
            handleClose(e);
            onView(row);
          }}
          sx={{ gap: 1.5, fontSize: "0.85rem" }}
        >
          <VisibilityIcon fontSize="small" color="primary" />
          {t("view", "View")}
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            handleClose(e);
            onEdit(row);
          }}
          sx={{ gap: 1.5, fontSize: "0.85rem" }}
        >
          <EditIcon fontSize="small" color="primary" />
          {t("edit", "Edit")}
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            handleClose(e);
            onDelete(row.id);
          }}
          sx={{ gap: 1.5, fontSize: "0.85rem" }}
        >
          <DeleteIcon fontSize="small" color="error" />
          {t("delete", "Delete")}
        </MenuItem>
        {row.status !== "ARCHIVED" && (
          <MenuItem
            onClick={(e) => {
              handleClose(e);
              onTogglePublish(row);
            }}
            sx={{ gap: 1.5, fontSize: "0.85rem" }}
          >
            {row.status === "PUBLISHED" ? (
              <>
                <PublicOffIcon fontSize="small" color="action" />
                {t("unpublish", "Unpublish")}
              </>
            ) : (
              <>
                <PublicIcon fontSize="small" color="success" />
                {t("publish", "Publish Now")}
              </>
            )}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const BlogManagement: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useError();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    useBlogsQuery,
    createBlogMutation,
    updateBlogMutation,
    toggleBlogPublishMutation,
    saveBlogAsDraftMutation,
    uploadBlogCoverImageMutation,
    deleteBlogMutation,
  } = useBlogs();

  const blogsQuery = useBlogsQuery(page, rowsPerPage, searchTerm);

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [selectedBlog, setSelectedBlog] = useState<Blog | null>(null);
  const [blogToDelete, setBlogToDelete] = useState<number | null>(null);
  const [submitMode, setSubmitMode] = useState<"save" | "draft">("save");
  const [coverImageFileName, setCoverImageFileName] = useState("");
  const [coverPreviewOpen, setCoverPreviewOpen] = useState(false);
  const [pendingCoverImageFile, setPendingCoverImageFile] = useState<File | null>(null);
  const [pendingCoverImagePreview, setPendingCoverImagePreview] = useState<string>("");
  const editorRef = useRef<HTMLDivElement | null>(null);
  const editorContentRef = useRef<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const todayStr = new Date().toISOString().split("T")[0];

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required(t("title_required", "Title is required") as string)
      .max(255, t("title_max", "Title cannot exceed 255 characters") as string),
    category: Yup.string()
      .required(t("category_required", "Category is required") as string)
      .max(100, t("category_max", "Category cannot exceed 100 characters") as string),
    coverImageUrl: Yup.string()
      .max(500, t("cover_image_url_max", "Cover Image URL cannot exceed 500 characters") as string)
      .nullable(),
    issueDate: Yup.string().required(t("issue_date_required", "Issue date is required") as string),
    description: Yup.string()
      .required(t("description_required", "Description is required") as string)
      .max(200, t("description_max", "Description cannot exceed 200 characters") as string),
    content: Yup.string().required(t("content_required", "Content is required") as string),
  });

  const formik = useFormik<BlogRequest>({
    initialValues: {
      title: "",
      category: "",
      coverImageUrl: "",
      issueDate: todayStr,
      description: "",
      content: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      // Sync latest editor content before submitting
      const latestContent = editorContentRef.current || editorRef.current?.innerHTML || "";
      values.content = latestContent;

      let finalValues = { ...values };
      if (pendingCoverImageFile) {
        try {
          const uploadResult = await uploadBlogCoverImageMutation.mutateAsync(pendingCoverImageFile);
          finalValues.coverImageUrl = normalizeUploadedImageUrl(uploadResult.url);
        } catch (uploadError: any) {
          const msg = uploadError?.response?.data?.message || uploadError?.message || "Failed to upload cover image.";
          showError(msg);
          return;
        }
      }

      try {
        let resultBlog: Blog;
        if (selectedBlog) {
          resultBlog = await updateBlogMutation.mutateAsync({
            id: selectedBlog.id,
            data: finalValues,
          });
          showSuccess(t("blog_updated", "Blog updated successfully!") || "Blog updated successfully!");
        } else {
          resultBlog = await createBlogMutation.mutateAsync(finalValues);
          showSuccess(t("blog_created", "Blog created successfully!") || "Blog created successfully!");
        }

        if (submitMode === "draft" && selectedBlog && resultBlog?.id) {
          await saveBlogAsDraftMutation.mutateAsync(resultBlog.id);
          showSuccess(t("blog_saved_as_draft", "Blog saved as draft!") || "Blog saved as draft!");
        }

        closeFormDialog();
      } catch (error: any) {
        const msg = error?.response?.data?.message || error?.message || "Operation failed.";
        showError(msg);
      }
    },
  });

  const openCreate = () => {
    setSelectedBlog(null);
    formik.resetForm({
      values: {
        title: "",
        category: "",
        coverImageUrl: "",
        issueDate: todayStr,
        description: "",
        content: "",
      },
    });
    setCoverImageFileName("");
    setPendingCoverImageFile(null);
    setPendingCoverImagePreview("");
    setSubmitMode("save");
    setFormDialogOpen(true);
  };

  const openEdit = (blog: Blog) => {
    setSelectedBlog(blog);
    
    let formattedIssueDate = todayStr;
    if (blog.issueDate) {
      if (Array.isArray(blog.issueDate)) {
        const [y, m, d] = blog.issueDate;
        formattedIssueDate = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      } else if (typeof blog.issueDate === "string") {
        if (blog.issueDate.includes(",")) {
          const parts = blog.issueDate.split(",").map(Number);
          if (parts.length >= 3) {
            formattedIssueDate = `${parts[0]}-${String(parts[1]).padStart(2, "0")}-${String(parts[2]).padStart(2, "0")}`;
          }
        } else {
          formattedIssueDate = blog.issueDate.split("T")[0];
        }
      }
    }

    formik.resetForm({
      values: {
        title: blog.title || "",
        category: blog.category || "",
        coverImageUrl: blog.coverImageUrl || "",
        issueDate: formattedIssueDate,
        description: blog.description || "",
        content: blog.content || "",
      },
    });
    setCoverImageFileName(blog.coverImageUrl ? String(t("existing_image", "Existing image")) : "");
    setPendingCoverImageFile(null);
    setPendingCoverImagePreview("");
    setSubmitMode("save");
    setFormDialogOpen(true);
  };

  const openView = (blog: Blog) => {
    setSelectedBlog(blog);
    setViewDialogOpen(true);
  };

  const openDeleteConfirm = (id: number) => {
    setBlogToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeFormDialog = () => {
    setFormDialogOpen(false);
    setSelectedBlog(null);
    setCoverImageFileName("");
    if (pendingCoverImagePreview) {
      URL.revokeObjectURL(pendingCoverImagePreview);
    }
    setPendingCoverImageFile(null);
    setPendingCoverImagePreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedBlog(null);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setBlogToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (blogToDelete === null) return;
    try {
      await deleteBlogMutation.mutateAsync(blogToDelete);
      showSuccess(t("blog_deleted", "Blog archived successfully!") || "Blog archived successfully!");
      closeDeleteDialog();
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Failed to delete blog.";
      showError(msg);
    }
  };

  const handleTogglePublishDirect = async (blog: Blog) => {
    try {
      const updatedBlog = await toggleBlogPublishMutation.mutateAsync(blog.id);
      showSuccess(
        updatedBlog.status === "PUBLISHED"
          ? t("blog_published", "Blog published successfully!") || "Blog published successfully!"
          : t("blog_unpublished", "Blog unpublished successfully!") || "Blog unpublished successfully!"
      );
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || "Failed to update publish status.";
      showError(msg);
    }
  };

  const handleCoverImageSelect = (file?: File) => {
    if (!file) return;
    if (pendingCoverImagePreview) {
      URL.revokeObjectURL(pendingCoverImagePreview);
    }
    const previewUrl = URL.createObjectURL(file);
    setPendingCoverImageFile(file);
    setPendingCoverImagePreview(previewUrl);
    setCoverImageFileName(file.name);
  };

  const handleRemoveCoverImage = () => {
    if (pendingCoverImagePreview) {
      URL.revokeObjectURL(pendingCoverImagePreview);
    }
    setPendingCoverImageFile(null);
    setPendingCoverImagePreview("");
    setCoverImageFileName("");
    formik.setFieldValue("coverImageUrl", "");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const execRichTextCommand = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    editorContentRef.current = editorRef.current?.innerHTML || "";
  };

  const handleContentInput = () => {
    editorContentRef.current = editorRef.current?.innerHTML || "";
  };

  const setEditorRef = useCallback((node: HTMLDivElement | null) => {
    editorRef.current = node;
    if (node) {
      // Only update innerHTML if it has changed to prevent cursor jumping
      if (node.innerHTML !== (formik.values.content || "")) {
        node.innerHTML = formik.values.content || "";
      }
      editorContentRef.current = formik.values.content || "";
    }
  }, [formik.values.content]);

  const columns = [
    {
      id: "title",
      label: t("title", "Title"),
      minWidth: 150,
      format: (val: any) => (
        <Typography fontWeight="bold" sx={{ fontSize: "0.9rem" }}>
          {String(val)}
        </Typography>
      ),
    },
    {
      id: "category",
      label: t("category", "Category"),
      minWidth: 120,
      format: (val: any) => (
        <Chip
          size="small"
          label={String(val)}
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            fontWeight: "bold",
          }}
        />
      ),
    },
    {
      id: "description",
      label: t("description", "Description"),
      minWidth: 200,
      format: (val: any) => {
        return truncateDescription(val);
      },
    },
    {
      id: "authorName",
      label: t("author", "Author"),
      minWidth: 120,
    },
    {
      id: "issueDate",
      label: t("issue_date", "Issue Date"),
      minWidth: 120,
      format: (val: any) => formatDateDdMmYyyy(val),
    },
    {
      id: "publishedAt",
      label: t("published_date", "Published Date"),
      minWidth: 140,
      format: (val: any) => formatDateDdMmYyyy(val),
    },
    {
      id: "status",
      label: t("status", "Status"),
      minWidth: 120,
      format: (val: any) => {
        const status = String(val ?? "DRAFT");
        let chipColor: "default" | "success" | "error" = "default";
        if (status === "PUBLISHED") chipColor = "success";
        if (status === "ARCHIVED") chipColor = "error";
        return <Chip size="small" label={getStatusLabel(status)} color={chipColor} variant="outlined" />;
      },
    },
  ];

  return (
    <Box>
      {(blogsQuery.isLoading ||
        createBlogMutation.isLoading ||
        updateBlogMutation.isLoading ||
        toggleBlogPublishMutation.isLoading ||
        saveBlogAsDraftMutation.isLoading ||
        uploadBlogCoverImageMutation.isLoading ||
        deleteBlogMutation.isLoading) && <LinearProgress sx={{ mb: 1 }} />}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          {t("blogs_management", "Blogs Management")}
        </Typography>
        <Button
          variant="contained"
          sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          onClick={openCreate}
        >
          {t("create_blog", "+ Create Blog")}
        </Button>
      </Box>

      {blogsQuery.isError && (
        <Box sx={{ mb: 1 }}>
          <Typography color="error" fontWeight="bold">
            {t("failed_load_blogs", "Failed to load blogs.")}
          </Typography>
        </Box>
      )}

      <EnhancedTable
        columns={columns}
        data={blogsQuery.data?.content ?? []}
        searchFields={["title", "category", "authorName", "description", "status"]}
        externalSearchTerm={searchTerm}
        hideSearch={false}
        enablePagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalElements={blogsQuery.data?.totalElements ?? 0}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        loading={blogsQuery.isLoading}
        cardProps={{
          elevation: 0,
          sx: {
            border: "1px solid #e0e0e0",
            "& .MuiCardContent-root": { p: 2 },
          },
        }}
        actions={(row: Blog) => (
          <BlogActionMenu
            row={row}
            onView={openView}
            onEdit={openEdit}
            onDelete={openDeleteConfirm}
            onTogglePublish={handleTogglePublishDirect}
          />
        )}
      />

      {/* Create / Edit Dialog */}
      <CommonModal
        open={formDialogOpen}
        onClose={closeFormDialog}
        title={selectedBlog ? t("edit_blog", "Edit Blog") : t("create_blog_title", "Create Blog")}
        maxWidth="md"
        customActions={
          <>
            <Button
              onClick={closeFormDialog}
              variant="outlined"
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              {t("cancel", "Cancel")}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => { setSubmitMode("draft"); void formik.submitForm(); }}
            >
              {t("save_as_draft", "Save as Unpublished")}
            </Button>
            <Button
              variant="contained"
              onClick={() => { setSubmitMode("save"); void formik.submitForm(); }}
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
            >
              {selectedBlog ? t("update", "Update") : t("create", "Create")}
            </Button>
          </>
        }
      >
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="title"
                  label={t("blog_title_star", "Blog Title *")}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  name="category"
                  label={t("category_star", "Category *")}
                  value={formik.values.category}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.category && Boolean(formik.errors.category)}
                  helperText={formik.touched.category && formik.errors.category}
                >
                  <MenuItem value="Announcement">{t("announcement", "Announcement")}</MenuItem>
                  <MenuItem value="News">{t("news", "News")}</MenuItem>
                  <MenuItem value="Guide">{t("guide", "Guide")}</MenuItem>
                  <MenuItem value="Update">{t("update", "Update")}</MenuItem>
                  <MenuItem value="Event">{t("event", "Event")}</MenuItem>
                  <MenuItem value="Other">{t("other", "Other")}</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box>
                  {/* Hidden file input — triggered programmatically */}
                  <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={(event) => {
                      handleCoverImageSelect(event.currentTarget.files?.[0]);
                      event.currentTarget.value = "";
                    }}
                  />

                  {(formik.values.coverImageUrl || pendingCoverImagePreview) ? (
                    /* ---- Has image: show thumbnail + change link ---- */
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 0,
                        width: "100%",
                        minHeight: 92,
                        p: 1,
                        border: "1px solid",
                        borderColor:
                          formik.touched.coverImageUrl && formik.errors.coverImageUrl
                            ? "error.main"
                            : "rgba(0, 0, 0, 0.23)",
                        borderRadius: 1,
                      }}
                    >
                      {/* Thumbnail — click to preview */}
                      <Box
                        onClick={() => setCoverPreviewOpen(true)}
                        sx={{
                          position: "relative",
                          flexShrink: 0,
                          cursor: "pointer",
                          borderRadius: 1,
                          overflow: "hidden",
                          width: 92,
                          height: 64,
                          border: "1px solid #e0e0e0",
                          bgcolor: "#f5f5f5",
                          "&:hover .preview-overlay": { opacity: 1 },
                        }}
                      >
                        <Box
                          component="img"
                          src={pendingCoverImagePreview || normalizeUploadedImageUrl(formik.values.coverImageUrl || "")}
                          alt={coverImageFileName || (t("cover_image_preview", "Cover image preview") as string)}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                          onError={(event: any) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                        {/* Hover overlay with zoom icon */}
                        <Box
                          className="preview-overlay"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(0,0,0,0.45)",
                            opacity: 0,
                            transition: "opacity 0.2s",
                          }}
                        >
                          <ZoomInIcon sx={{ color: "#fff", fontSize: 28 }} />
                        </Box>
                      </Box>

                      {/* Filename + change link — click to open file picker */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexGrow: 1,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{ minWidth: 0, textAlign: "left", cursor: "pointer", flexGrow: 1 }}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ pr: 1 }}>
                            {coverImageFileName || t("cover_image", "Cover Image")}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "primary.main",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {t("click_to_change_image", "Click to change image")}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCoverImage();
                          }}
                          title={t("remove_image", "Remove Image") as string}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    /* ---- No image yet: simple upload button ---- */
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        minHeight: 56,
                        justifyContent: "flex-start",
                        color: "text.secondary",
                        borderColor:
                          formik.touched.coverImageUrl && formik.errors.coverImageUrl
                            ? "error.main"
                            : "rgba(0, 0, 0, 0.23)",
                        textTransform: "none",
                      }}
                    >
                      {t("upload_cover_image", "Upload Cover Image")}
                    </Button>
                  )}

                  {formik.touched.coverImageUrl && formik.errors.coverImageUrl && (
                    <Typography color="error" variant="caption" sx={{ display: "block", mt: 0.5, ml: 1.5 }}>
                      {formik.errors.coverImageUrl}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  name="issueDate"
                  label={t("issue_date_star", "Issue Date *")}
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.issueDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.issueDate && Boolean(formik.errors.issueDate)}
                  helperText={formik.touched.issueDate && formik.errors.issueDate}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  name="description"
                  label={t("description_star", "Description *")}
                  placeholder={t("description_placeholder", "Short summary / excerpt shown in listing (max 200 chars)")}
                  inputProps={{ maxLength: 200 }}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={
                    (formik.touched.description && formik.errors.description) ||
                    `${formik.values.description?.length ?? 0}/200`
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.75 }}>
                    {t("content_star", "Content *")}
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: formik.touched.content && formik.errors.content ? "error.main" : "rgba(0, 0, 0, 0.23)",
                      borderRadius: 1,
                      overflow: "hidden",
                      "&:focus-within": {
                        borderColor: "primary.main",
                        borderWidth: 2,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        p: 1,
                        borderBottom: "1px solid #e0e0e0",
                        bgcolor: "#fafafa",
                        flexWrap: "wrap",
                      }}
                    >
                      <IconButton size="small" onClick={() => execRichTextCommand("bold")} title={t("bold", "Bold") as string}>
                        <FormatBoldIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => execRichTextCommand("italic")} title={t("italic", "Italic") as string}>
                        <FormatItalicIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => execRichTextCommand("underline")} title={t("underline", "Underline") as string}>
                        <FormatUnderlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => execRichTextCommand("formatBlock", "H2")} title={t("heading", "Heading") as string}>
                        <TitleIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => execRichTextCommand("insertUnorderedList")} title={t("bulleted_list", "Bulleted list") as string}>
                        <FormatListBulletedIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => execRichTextCommand("insertOrderedList")} title={t("numbered_list", "Numbered list") as string}>
                        <FormatListNumberedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box
                      ref={setEditorRef}
                      contentEditable
                      suppressContentEditableWarning
                      dir="ltr"
                      onInput={handleContentInput}
                      onBlur={() => {
                        formik.setFieldValue("content", editorContentRef.current);
                        formik.setFieldTouched("content", true);
                      }}
                      sx={{
                        minHeight: 220,
                        p: 2,
                        outline: "none",
                        bgcolor: "#fff",
                        textAlign: "left",
                        direction: "ltr",
                        unicodeBidi: "plaintext",
                        "& ul, & ol": { pl: 3 },
                        "& h1, & h2, & h3": { mt: 1, mb: 1 },
                      }}
                    />
                  </Box>
                  {formik.touched.content && formik.errors.content && (
                    <Typography color="error" variant="caption" sx={{ display: "block", mt: 0.5, ml: 1.5 }}>
                      {formik.errors.content}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
      </CommonModal>

      {/* View Blog Details Dialog */}
      <CommonModal
        open={viewDialogOpen}
        onClose={closeViewDialog}
        title={t("view_blog_details", "View Blog Details")}
        maxWidth="md"
        hideActions
      >
          <Box sx={{ px: 1 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
              {selectedBlog?.title}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
              <Chip
                label={selectedBlog?.category}
                size="small"
                sx={{ backgroundColor: "#1976d2", color: "#fff", fontWeight: "bold" }}
              />
              <Chip
                label={getStatusLabel(selectedBlog?.status)}
                size="small"
                color={
                  selectedBlog?.status === "PUBLISHED"
                    ? "success"
                    : selectedBlog?.status === "ARCHIVED"
                    ? "error"
                    : "default"
                }
                variant="outlined"
              />
            </Box>

            {selectedBlog?.coverImageUrl && (
              <Box sx={{ mb: 3, borderRadius: 2, overflow: "hidden", border: "1px solid #e0e0e0" }}>
                <CardMedia
                  component="img"
                  image={normalizeUploadedImageUrl(selectedBlog.coverImageUrl)}
                  alt={selectedBlog.title}
                  sx={{ maxHeight: 200, width: "100%", objectFit: "cover" }}
                  onError={(e: any) => {
                    e.target.style.display = "none";
                  }}
                />
              </Box>
            )}

            <Grid container spacing={2} sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary" display="block">
                  {t("issue_date", "Issue Date")}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatDateDdMmYyyy(selectedBlog?.issueDate)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary" display="block">
                  {t("published_date", "Published Date")}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatDateDdMmYyyy(selectedBlog?.publishedAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary" display="block">
                  {t("author", "Author")}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedBlog?.authorName || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="textSecondary" display="block">
                  {t("views", "Views")}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedBlog?.viewsCount ?? 0}
                </Typography>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ fontStyle: "italic", color: "textSecondary", mb: 3 }}>
              {selectedBlog?.description}
            </Typography>

            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              {t("blog_content", "Blog Content")}
            </Typography>
            <Box
              sx={{
                p: 2.5,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                maxHeight: 300,
                overflowY: "auto",
                backgroundColor: "#fafafa",
                fontFamily: "inherit",
                "& ul, & ol": { pl: 3 },
              }}
              dangerouslySetInnerHTML={{ __html: selectedBlog?.content || "" }}
            />
          </Box>
      </CommonModal>

      {/* Cover Image Preview Dialog */}
      <CommonModal
        open={coverPreviewOpen}
        onClose={() => setCoverPreviewOpen(false)}
        title={t("cover_image_preview", "Cover Image Preview")}
        maxWidth="md"
        hideActions
        contentSx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, paddingTop: '16px !important' }}
      >
        <Box
          component="img"
          src={normalizeUploadedImageUrl(formik.values.coverImageUrl || "")}
          alt={t("cover_image_preview", "Cover image preview") as string}
          sx={{
            display: "block",
            maxWidth: "80vw",
            maxHeight: "80vh",
            borderRadius: 2,
            objectFit: "contain",
          }}
        />
      </CommonModal>

      {/* Delete Confirmation Dialog */}
      <CommonModal
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        title={t("confirm_delete", "Confirm Delete")}
        maxWidth="xs"
        customActions={
          <>
            <Button
              onClick={closeDeleteDialog}
              variant="outlined"
              sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
            >
              {t("cancel", "Cancel")}
            </Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              {t("delete", "Delete")}
            </Button>
          </>
        }
      >
        <Typography color="text.secondary">
          {t("confirm_delete_blog_text", "Are you sure you want to archive this blog post?")}
        </Typography>
      </CommonModal>
    </Box>
  );
};

export default BlogManagement;
